import {
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  Heading,
  Spinner,
  ModalFooter,
  Box,
  Button,
  Text,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import {
  AdminBookingUpdateProps,
  RequestProps,
  StudentModelProps,
} from "../types";
import { transformRequests } from "../utils";
import RequestModalContent from "./components/RequestModalContent";

export const AdminBookingUpdate = ({
  linkRequest,
  updateStudentDetails,
  getRequestsData,
  onClose: onCloseFn,
}: AdminBookingUpdateProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [data, setData] = useState<{
    students: StudentModelProps[];
    requests: RequestProps[];
  }>({ students: [], requests: [] });
  const [loading, setLoading] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(0);
  const [requestLoading, setRequestLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [forceRefresh, setForceRefresh] = useState(0);

  useEffect(() => {
    let mounted = true;
    onOpen();

    (async () => {
      setError("");
      try {
        const data = await getRequestsData();
        if (mounted) {
          setData({
            students: data.students,
            requests: transformRequests(data.requests),
          });
          if (data.booking?.linked_request)
            setSelectedRequestId(data.booking.linked_request);
        }
      } catch (e) {
        const message = (e as Error).message;
        mounted && setError(message ?? e);
      }
      setRequestLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const refreshRequests = async (requestId?: number) => {
    setRequestLoading(true);
    setError("");

    try {
      const data = await getRequestsData();
      setData({
        students: data.students,
        requests: transformRequests(data.requests),
      });
      if (requestId) setSelectedRequestId(requestId);
    } catch (e) {
      setData({ students: [], requests: [] }); // force data reset
      const message = (e as Error).message;
      setError(message ?? e);
    }
    setRequestLoading(false);
  };

  const handleLinkRequest = async (requestId: number) => {
    setLoading(true);
    try {
      await linkRequest(requestId);
      setSuccess("Request linked");
    } catch (e) {
      const message = (e as Error).message;
      setError(message ?? "Error occurred while processing this action");
    }
    setLoading(false);
  };

  const handleClosingModal = () => {
    onCloseFn?.();
    onClose();
  };

  useEffect(() => {
    let t: NodeJS.Timeout;
    if (success) {
      t = setTimeout(() => {
        setSuccess("");
      }, 3000);
    }

    return () => {
      clearTimeout(t);
    };
  }, [success]);

  useEffect(() => {
    if (forceRefresh) {
      refreshRequests(forceRefresh);
    }
  }, [forceRefresh]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClosingModal} trapFocus={true}>
        <ModalOverlay />
        <ModalContent minW={"500px"} py="1rem">
          <ModalCloseButton mt="1rem" />
          <ModalHeader>
            <Heading size="lg" mb="1rem">
              Link request to booking
            </Heading>
          </ModalHeader>
          <ModalBody>
            {requestLoading ? (
              <Box
                display="flex"
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Box>
            ) : (
              <RequestModalContent
                students={data.students}
                requests={data.requests}
                setSelectedRequestId={setSelectedRequestId}
                selectedRequestId={selectedRequestId}
                updateStudentDetails={updateStudentDetails}
                refreshRequests={setForceRefresh}
              />
            )}
            {error && (
              <Text mb="1rem" mt="-1rem" fontSize="sm" color="tomato">
                {error}
              </Text>
            )}
            {success && (
              <Text mb="1rem" mt="-1rem" fontSize="sm" color="#3caf50">
                {success}
              </Text>
            )}
          </ModalBody>
          <ModalFooter display={"block"}>
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              w={"full"}
            >
              <Button
                onClick={() => refreshRequests()}
                size="sm"
                variant={"ghost"}
                fontWeight="normal"
              >
                Refresh
              </Button>
              <Button
                colorScheme={"green"}
                type="submit"
                isLoading={loading}
                loadingText="linking"
                onClick={() => handleLinkRequest(selectedRequestId)}
                disabled={!selectedRequestId}
              >
                Link request
              </Button>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
